import React, { useState, useEffect } from "react";
import Switch, { SwitchProps } from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { styled } from "@mui/material/styles";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import micDarkIcon from "../assets/icons/mic_waveform_dark.svg";
import micLightIcon from "../assets/icons/mic_waveform_light.svg";
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

interface LeftTabProps {
  handleTabSelect: (tab: string) => void;
  selectedTab: string;
  isLeftTabCollapsed: boolean;
}

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 70,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(30px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 25 25"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
    width: 32,
    height: 32,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 25 25"><path fill="${encodeURIComponent(
        "#fff"
      )}" d="M14.69 2.21 4.33 11.49c-.64.58-.28 1.65.58 1.73L13 14l-4.85 6.76c-.22.31-.19.74.08 1.01.3.3.77.31 1.08.02l10.36-9.28c.64-.58.28-1.65-.58-1.73L11 10l4.85-6.76c.22-.31.19-.74-.08-1.01a.77.77 0 0 0-1.08-.02z"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
    borderRadius: 20 / 2,
  },
}));

const LeftTab: React.FC<LeftTabProps> = ({ handleTabSelect, selectedTab, isLeftTabCollapsed }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isAdvanced, setIsAdvanced] = useState(true);
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
        fetchAndSetModelMode(user.uid);
      } else {
        console.log("User not authenticated.");
        setUid(null);
      }
    });

    const fetchAndSetModelMode = async (uid: string) => {
      try {
        const userDocRef = doc(db, "users", uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          if (userData.modelMode) {
            setIsAdvanced(userData.modelMode === "Advanced");
          } else {
            console.log("User model mode not found, defaulting to Advanced.");
            await updateDoc(userDocRef, { modelMode: "Advanced" });
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    return () => {
      unsubscribe();
    };
  }, []);

  // Function to handle mouse entering the div (hover start)
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  // Function to handle mouse leaving the div (hover end)
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newMode = event.target.checked ? "Advanced" : "Turbo";
    setIsAdvanced(event.target.checked);

    if (uid) {
      try {
        const userDocRef = doc(db, "users", uid);
        await updateDoc(userDocRef, { modelMode: newMode });
        console.log("Model mode updated successfully to:", newMode);
      } catch (error) {
        console.error("Error updating model mode:", error);
      }
    } else {
      console.error("User ID not found, unable to update model mode.");
    }
  };

  return (
    // <div className="box-sizing-default left-tab-container">
      <div
        className={`box-sizing-default left-tab-container ${
          isLeftTabCollapsed ? "collapsed" : ""
        }`}
      >
        {/* <div className={`left-tab ${isTabCollapsed ? "collapsed" : ""}`}> */}
        <div>
          <div
            className={`box-sizing-default left-tab-item ${
              selectedTab === "transcribe" ? "active" : ""
            }`}
            style={{
              display: "flex",
              alignItems: "center",
              alignContent: "center",
              fontSize: "1.5rem",
              marginBottom: "1.5rem",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleTabSelect("transcribe")}
          >
            <img
              src={
                isHovering || selectedTab === "transcribe"
                  ? micDarkIcon
                  : micLightIcon
              }
              alt="Start Session"
              style={{
                marginRight: "0.2rem",
                width: "1.7rem",
                height: "1.7rem",
              }}
            />
            <span>Start Session</span>
          </div>
          {/* <div
          className={`box-sizing-default left-tab-item ${
            selectedTab === "patients" ? "active" : ""
          }`}
          autoFocus
          onClick={() => handleTabSelect("patients")}
        >
          {"Patients"}
        </div> */}
          <div
            className={`box-sizing-default left-tab-item ${
              selectedTab === "history" ? "active" : ""
            }`}
            autoFocus
            onClick={() => handleTabSelect("history")}
          >
            {"History"}
          </div>
          <div
            className={`box-sizing-default left-tab-item ${
              selectedTab === "templates" ? "active" : ""
            }`}
            autoFocus
            onClick={() => handleTabSelect("templates")}
          >
            {"Templates"}
          </div>
          <div
            className={`box-sizing-default left-tab-item ${
              selectedTab === "search" ? "active" : ""
            }`}
            autoFocus
            onClick={() => handleTabSelect("search")}
          >
            {"Search Guidelines"}
          </div>
          <div
            className={`box-sizing-default left-tab-item ${
              selectedTab === "help" ? "active" : ""
            }`}
            autoFocus
            onClick={() => handleTabSelect("help")}
          >
            {"Help"}
          </div>
        </div>
      <div>
        {/* <FormGroup style={{ color: "white", textAlign: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Copilot Mode
            <Tooltip title="Switch between Turbo and Advanced modes to change the model's behavior. Advanced mode prioritises note accuracy while Turbo mode prioritises speed.">
              <IconButton size="small">
                <HelpOutlineIcon sx={{ color: "white", fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </div>

          <FormControlLabel
            control={
              // <Switch
              //   checked={isAdvanced}
              //   onChange={handleModeChange}
              //   defaultChecked
              //   color="default"
              // />
              <MaterialUISwitch
                sx={{ m: 1 }}
                checked={isAdvanced}
                onChange={handleModeChange}
              />
            }
            label={isAdvanced ? "Advanced" : "Turbo"}
            labelPlacement="bottom"
            sx={{
              "& .MuiFormControlLabel-label": {
                color: "white",
                fontSize: "0.8rem",
              },
            }}
          />
        </FormGroup> */}
      </div>
    </div>
  );
};

export default LeftTab;
